import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { Button } from './ui/button'
import { reconciliationApi, ClientData, UploadBankStatementResponse } from '../lib/api'
import { isDemoMode } from '@/demo/config'
import { useToast } from '../hooks/use-toast'
import { ClientDropdown } from './ui/client-dropdown'
import { Trash2, Loader2, CheckCircle2, AlertCircle, Upload } from 'lucide-react'
import { DuplicateFileDialog, DuplicateFileInfo } from './DuplicateFileDialog'

const STORAGE_KEY = 'bank-statement-upload-statuses'

type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'failed'

interface UploadItem {
  id: string
  name: string
  file?: File
  fileId?: string
  clientId: string
  status: UploadStatus
  message?: string
  error?: string
  lastUpdated?: number
}

interface StoredUploadItem {
  id: string
  fileId: string
  name: string
  clientId: string
  status: UploadStatus
  message?: string
  lastUpdated?: number
}

const createLocalId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

interface UploadBankStatementProps {
  clients?: ClientData[] // Optional, not used in this component
  selectedClient: string
  onClientChange: (clientId: string) => void
  onUploadComplete: () => void
}

export const UploadBankStatement: React.FC<UploadBankStatementProps> = ({
  selectedClient,
  onClientChange,
  onUploadComplete,
}) => {
  const { toast } = useToast()
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const removalTimeouts = useRef<Record<string, number>>({})
  const uploadItemsRef = useRef<UploadItem[]>([])
  const isCheckingRef = useRef(false)
  const hasProcessingRef = useRef(false)

  // Duplicate file dialog state
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean
    existingFile: DuplicateFileInfo | null
    pendingItem: UploadItem | null
  }>({
    open: false,
    existingFile: null,
    pendingItem: null,
  })

  const persistUploads = useCallback((items: UploadItem[]) => {
    if (typeof window === 'undefined') return

    const toPersist: StoredUploadItem[] = items
      .filter(item => item.fileId && (item.status === 'processing' || item.status === 'uploading'))
      .map(item => ({
        id: item.id,
        fileId: item.fileId!,
        name: item.name,
        clientId: item.clientId,
        status: item.status === 'uploading' ? 'processing' : item.status,
        message: item.message,
        lastUpdated: item.lastUpdated,
      }))

    if (toPersist.length > 0) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist))
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const setUploadItemsState = useCallback(
    (updater: UploadItem[] | ((prev: UploadItem[]) => UploadItem[])) => {
      setUploadItems(prev => {
        const next =
          typeof updater === 'function' ? (updater as (prev: UploadItem[]) => UploadItem[])(prev) : updater
        persistUploads(next)
        return next
      })
    },
    [persistUploads]
  )

  const updateItem = useCallback(
    (id: string, updates: Partial<UploadItem>) => {
      setUploadItemsState(prev =>
        prev.map(item =>
          item.id === id
            ? {
              ...item,
              ...updates,
            }
            : item
        )
      )
    },
    [setUploadItemsState]
  )

  const scheduleRemoval = useCallback(
    (id: string, delayMs = 3000) => {
      if (removalTimeouts.current[id]) return

      removalTimeouts.current[id] = window.setTimeout(() => {
        setUploadItemsState(prev => prev.filter(item => item.id !== id))
        window.clearTimeout(removalTimeouts.current[id])
        delete removalTimeouts.current[id]
      }, delayMs)
    },
    [setUploadItemsState]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    try {
      const stored: StoredUploadItem[] = JSON.parse(raw)
      if (!Array.isArray(stored)) {
        return
      }

      setUploadItemsState(prev => {
        const restored: UploadItem[] = stored.map(item => ({
          id: item.id || item.fileId,
          name: item.name,
          clientId: item.clientId,
          fileId: item.fileId,
          status: (item.status === 'completed' ? 'completed' : 'processing') as UploadStatus,
          message: item.message,
          lastUpdated: item.lastUpdated,
        }))

        const existingIds = new Set(restored.map(item => item.id))
        const merged: UploadItem[] = [...restored, ...prev.filter(item => !existingIds.has(item.id))]
        return merged
      })
    } catch (error) {
      console.error('Failed to restore bank statement upload state', error)
      window.localStorage.removeItem(STORAGE_KEY)
    }
  }, [setUploadItemsState])

  useEffect(() => {
    uploadItemsRef.current = uploadItems
  }, [uploadItems])

  useEffect(() => {
    return () => {
      Object.values(removalTimeouts.current).forEach(timeoutId => window.clearTimeout(timeoutId))
    }
  }, [])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!selectedClient) {
        toast({
          title: 'Client not selected',
          description: 'Please select a client before uploading files.',
          variant: 'destructive',
        })
        return
      }

      if (acceptedFiles.length === 0) {
        return
      }

      setUploadItemsState(prev => [
        ...prev,
        ...acceptedFiles.map(file => ({
          id: createLocalId(),
          name: file.name,
          file,
          clientId: selectedClient,
          status: 'pending' as UploadStatus,
          message: 'Ready to upload',
          lastUpdated: Date.now(),
        })),
      ])
    },
    [selectedClient, setUploadItemsState, toast]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // Demo mode: clicking the dropzone adds a scripted file instead of opening the OS picker.
    noClick: isDemoMode,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
    },
  })

  const handleDemoDropzoneClick = useCallback(() => {
    if (!isDemoMode) return
    onDrop([new File([new Blob(['demo'])], 'unicredit_2026-07.xml', { type: 'text/xml' })])
  }, [onDrop])

  const removeFile = useCallback(
    (id: string) => {
      setUploadItemsState(prev => prev.filter(item => item.id !== id))
    },
    [setUploadItemsState]
  )

  const uploadableItemsForClient = useMemo(
    () =>
      uploadItems.filter(
        item =>
          item.clientId === selectedClient &&
          (item.status === 'pending' || (item.status === 'failed' && item.file))
      ),
    [uploadItems, selectedClient]
  )

  const visibleItems = useMemo(
    () => uploadItems.filter(item => item.clientId === selectedClient),
    [uploadItems, selectedClient]
  )

  const hasProcessingForClient = useMemo(
    () =>
      uploadItems.some(
        item =>
          item.clientId === selectedClient && (item.status === 'uploading' || item.status === 'processing')
      ),
    [uploadItems, selectedClient]
  )

  // Handle duplicate file reprocess
  const handleReprocess = useCallback(async (force: boolean) => {
    if (!duplicateDialog.existingFile || !duplicateDialog.pendingItem) return

    const existingFileId = duplicateDialog.existingFile.id
    const pendingItem = duplicateDialog.pendingItem

    try {
      // Delete the existing file first
      await reconciliationApi.reprocessDuplicateFile(existingFileId, force)

      // Close dialog
      setDuplicateDialog({ open: false, existingFile: null, pendingItem: null })

      // Now re-upload the file
      if (pendingItem.file) {
        updateItem(pendingItem.id, { status: 'uploading', message: 'Uploading…', lastUpdated: Date.now() })

        const response = await reconciliationApi.uploadBankStatement(
          pendingItem.file,
          selectedClient
        )

        const fileId = response.file_id

        if (fileId) {
          updateItem(pendingItem.id, {
            status: 'processing',
            fileId,
            file: undefined,
            message: response.message || 'Processing file…',
            lastUpdated: Date.now(),
          })

          toast({
            title: 'File reprocessing started',
            description: 'The file is being processed in the background.',
          })
        } else {
          throw new Error('Upload succeeded but file ID was not returned.')
        }
      }
    } catch (error) {
      console.error('Failed to reprocess file:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'

      updateItem(pendingItem.id, {
        status: 'failed',
        message: 'Reprocess failed',
        error: errorMessage,
        lastUpdated: Date.now(),
      })

      toast({
        title: 'Reprocess failed',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }, [duplicateDialog.existingFile, duplicateDialog.pendingItem, selectedClient, toast, updateItem])

  // Handle cancel duplicate dialog
  const handleCancelDuplicate = useCallback(() => {
    if (duplicateDialog.pendingItem) {
      // Mark the item as completed/skipped
      updateItem(duplicateDialog.pendingItem.id, {
        status: 'completed',
        message: 'Skipped (duplicate)',
        lastUpdated: Date.now(),
      })
      scheduleRemoval(duplicateDialog.pendingItem.id, 2000)
    }
    setDuplicateDialog({ open: false, existingFile: null, pendingItem: null })
  }, [duplicateDialog.pendingItem, updateItem, scheduleRemoval])

  const handleUpload = useCallback(async () => {
    if (!selectedClient) {
      toast({
        title: 'Client not selected',
        description: 'Please select a client before uploading.',
        variant: 'destructive',
      })
      return
    }

    const filesToUpload = uploadItems.filter(
      item =>
        item.clientId === selectedClient &&
        (item.status === 'pending' || item.status === 'failed') &&
        item.file
    )

    if (filesToUpload.length === 0) {
      toast({
        title: 'No files ready',
        description: 'Add at least one file for this client before uploading.',
        variant: 'destructive',
      })
      return
    }

    setIsUploading(true)

    let queuedCount = 0
    let duplicateCount = 0
    let failedCount = 0

    for (const item of filesToUpload) {
      updateItem(item.id, { status: 'uploading', message: 'Uploading…', lastUpdated: Date.now() })

      try {
        const response: UploadBankStatementResponse = await reconciliationApi.uploadBankStatement(
          item.file!,
          selectedClient
        )

        const fileId = response.file_id || response.existing_file?.id

        if (response.duplicate && response.existing_file) {
          duplicateCount += 1
          // Show dialog for duplicate file handling
          updateItem(item.id, {
            status: 'pending',
            message: 'Duplicate detected - awaiting decision',
            lastUpdated: Date.now(),
          })

          setDuplicateDialog({
            open: true,
            existingFile: response.existing_file as DuplicateFileInfo,
            pendingItem: item,
          })

          // Stop processing more files until dialog is resolved
          break
        } else if (fileId) {
          queuedCount += 1
          updateItem(item.id, {
            status: 'processing',
            fileId,
            file: undefined,
            message: response.message || 'Processing file…',
            lastUpdated: Date.now(),
          })
        } else {
          throw new Error('Upload succeeded but file ID was not returned.')
        }
      } catch (error) {
        failedCount += 1
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        updateItem(item.id, {
          status: 'failed',
          message: 'Upload failed',
          error: errorMessage,
          lastUpdated: Date.now(),
        })
        toast({
          title: `Failed to upload ${item.name}`,
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    if (queuedCount > 0) {
      toast({
        title: 'Files queued for processing',
        description: `${queuedCount} file(s) are being processed in the background.`,
      })
    }

    // Note: We no longer show a toast for duplicates since we show a dialog instead

    if (failedCount > 0 && queuedCount === 0 && duplicateCount === 0) {
      toast({
        title: 'Upload failed',
        description: `${failedCount} file(s) could not be uploaded.`,
        variant: 'destructive',
      })
    }

    setIsUploading(false)
  }, [selectedClient, toast, uploadItems, updateItem, scheduleRemoval, onUploadComplete])

  const checkStatuses = useCallback(async () => {
    const itemsToPoll = uploadItemsRef.current.filter(item => item.status === 'processing' && item.fileId)

    if (itemsToPoll.length === 0 || isCheckingRef.current) {
      return
    }

    isCheckingRef.current = true

    try {
      await Promise.all(
        itemsToPoll.map(async item => {
          try {
            const status = await reconciliationApi.getBankStatementProcessingStatus(item.fileId!)
            const normalizedStatus =
              status.status === 'failed'
                ? 'failed'
                : status.status === 'completed' || status.transactions_count > 0
                  ? 'completed'
                  : 'processing'

            const latestItem = uploadItemsRef.current.find(current => current.id === item.id)

            if (normalizedStatus === 'completed') {
              updateItem(item.id, {
                status: 'completed',
                message: status.message,
                lastUpdated: Date.now(),
              })
              onUploadComplete()
              scheduleRemoval(item.id)
            } else if (normalizedStatus === 'failed' && latestItem?.status !== 'failed') {
              updateItem(item.id, {
                status: 'failed',
                message: status.message,
                error: status.message,
                lastUpdated: Date.now(),
              })
              toast({
                title: `Processing failed for ${item.name}`,
                description: status.message,
                variant: 'destructive',
              })
            } else if (normalizedStatus === 'processing') {
              updateItem(item.id, {
                status: 'processing',
                message: status.message,
                lastUpdated: Date.now(),
              })
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unable to fetch processing status'
            const latestItem = uploadItemsRef.current.find(current => current.id === item.id)

            if (latestItem?.status !== 'failed') {
              updateItem(item.id, {
                status: 'failed',
                message: 'Processing status check failed',
                error: errorMessage,
                lastUpdated: Date.now(),
              })
              toast({
                title: `Failed to check status for ${item.name}`,
                description: errorMessage,
                variant: 'destructive',
              })
            }
          }
        })
      )
    } finally {
      isCheckingRef.current = false
    }
  }, [updateItem, scheduleRemoval, toast, onUploadComplete])

  useEffect(() => {
    const hasProcessing = uploadItems.some(item => item.status === 'processing' && item.fileId)

    if (hasProcessing) {
      if (!hasProcessingRef.current) {
        hasProcessingRef.current = true
        checkStatuses()
      }
    } else {
      hasProcessingRef.current = false
    }
  }, [uploadItems, checkStatuses])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const intervalId = window.setInterval(() => {
      checkStatuses()
    }, 2000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [checkStatuses])

  const renderStatus = (item: UploadItem) => {
    switch (item.status) {
      case 'pending':
        return (
          <span className="text-xs text-gray-500">Pending upload</span>
        )
      case 'uploading':
        return (
          <span className="flex items-center gap-1 text-xs text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading…
          </span>
        )
      case 'processing':
        return (
          <span className="flex items-center gap-1 text-xs text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            Processing…
          </span>
        )
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            Processed
          </span>
        )
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3 w-3" />
            Failed
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="w-full bg-gray-100 rounded-2xl p-6">
      <div
        {...getRootProps({ onClick: handleDemoDropzoneClick })}
        className={`flex flex-col gap-4 items-center justify-center border border-dashed rounded-lg p-6 w-full cursor-pointer transition-colors ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-500 hover:border-gray-400'
          }`}
      >
        <input {...getInputProps()} />
        <div className="max-w-xl w-full min-h-28"
        >
          <div className="flex flex-col gap-3 p-8">
            {isDragActive ? (
              <p className="text-gray-600 text-center text-sm">Drop the files here…</p>
            ) : visibleItems.length > 0 ? (
              <div className="space-y-2">
                {visibleItems.map(item => {
                  const canRemove = item.status === 'pending' || item.status === 'failed'

                  return (
                    <div key={item.id} className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-white">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {renderStatus(item)}
                          <span className="text-xs text-gray-600 break-all">{item.name}</span>
                        </div>
                        {(item.message || item.error) && (
                          <span className="text-[11px] text-gray-500">
                            {item.error || item.message}
                          </span>
                        )}
                      </div>
                      {canRemove && (
                        <button
                          onClick={() => removeFile(item.id)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center flex flex-col items-center gap-4">
                <div className="flex flex-col w-10 h-10 items-center justify-center">
                  <Upload className={`w-10 h-10 ${isDragActive ? 'text-blue-500' : 'text-gray-500'}`} />
                </div>
                <p className="text-lg font-semibold">Drag & Drop Bank Statements Here</p>
                <p className="text-sm text-gray-500">Supports CSV, XLS, XLSX, PDF</p>
              </div>
            )}
          </div>
        </div>

        <div
          className="flex flex-col sm:flex-row justify-between items-stretch sm:items-end gap-3 w-full max-w-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Client</label>
            <ClientDropdown
              value={selectedClient}
              onValueChange={onClientChange}
              placeholder="Select Client"
              multiple={false}
              disabled={hasProcessingForClient}
            />
          </div>

          <Button
            onClick={handleUpload}
            disabled={
              isUploading || !selectedClient || uploadableItemsForClient.length === 0
            }
            className="flex-2 bg-[#0000FF] text-white font-light hover:bg-blue-700 disabled:bg-gray-400 px-8"
          >
            {isUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </div>

      {/* Duplicate File Dialog */}
      <DuplicateFileDialog
        open={duplicateDialog.open}
        existingFile={duplicateDialog.existingFile}
        pendingFileName={duplicateDialog.pendingItem?.name ?? ''}
        onCancel={handleCancelDuplicate}
        onReprocess={handleReprocess}
      />
    </div>
  )
}
